// Angular imports
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';

// cc-common imports
import {
  IconConstants,
  CCDeviceService,
  DxWidgetUtils,
  Utils
} from '@therealmanage/ciranet-ui-common';

// 3rd party imports
import {
  ImageCropperComponent,
  ImageCroppedEvent,
  ImageTransform
} from 'ngx-image-cropper';
import { WebcamUtil } from 'ngx-webcam';

// devextreme imports
import notify from 'devextreme/ui/notify';
import { DxFileUploaderComponent } from 'devextreme-angular';

@Component({
  selector: 'app-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  standalone: false
})
export class ResidentImageEditorComponent implements OnInit {
  @ViewChild('fileUploader', { static: false })
  fileUploader: DxFileUploaderComponent;
  @ViewChild('ccImageCropper', { static: false })
  imageCropper: ImageCropperComponent;

  @Input() width: string | number = '60%';
  @Input() height: string | number = '40%';
  @Input() title = '';
  @Input() allowDrag = true;
  @Input() allowResize = false;
  @Input() allowWebcam = true;
  @Input() showCloseButton = true;

  @Output() imageEdited = new EventEmitter<string>();

  visible = false;
  disableAction = true;
  selectedImageFile: File;
  selectedImageBase64: string;
  showCropper = false;
  webcamAvailable = false;
  webcamVisible = false;
  isIOSDevice = false;
  webcamInfoVisible = false;
  transform: ImageTransform = {};

  images: File[] = [];
  imageChangedEvent: any = '';

  iconCancel = IconConstants.CANCEL;
  iconSave = IconConstants.SAVE;

  allowedFileExtensions: string[] = ['.jpg', '.jpeg', '.gif', '.png'];
  allowedFileExtensionsString: string;

  constructor(private readonly deviceService: CCDeviceService) {
    this.rotateRight = this.rotateRight.bind(this);
    this.rotateLeft = this.rotateLeft.bind(this);
    this.flipHorizontal = this.flipHorizontal.bind(this);
    this.flipVertical = this.flipVertical.bind(this);
  }

  ngOnInit(): void {
    this.isIOSDevice =
      this.deviceService.isIPad() || this.deviceService.isIPhone();

    if (Utils.isTrue(this.allowWebcam)) {
      WebcamUtil.getAvailableVideoInputs()
        .then((mediaDevices: MediaDeviceInfo[]) => {
          this.webcamAvailable = mediaDevices && mediaDevices.length > 0;
        })
        .catch((err) => {
          notify(err, 'error');
        });
    }

    this.allowedFileExtensionsString = this.allowedFileExtensions.join(',');
  }

  show(): void {
    this.clearImage();
    this.visible = true;
  }

  hide(): void {
    this.visible = false;
  }

  hiding(): void {
    this.selectedImageFile = null;
    this.selectedImageBase64 = null;
    this.showCropper = false;
  }

  showTitle(): boolean {
    return Utils.notNullOrEmpty(this.title);
  }

  onImageFileSelected(event: any): void {
    this.selectedImageFile = null;
    this.selectedImageBase64 = null;
    if (event.value != null && event.value.length > 0) {
      this.selectedImageFile = event.value[0];
      this.disableAction = false;
    }
    this.imageChangedEvent = event;
  }

  async imageCropped(event: ImageCroppedEvent): Promise<void> {
    const base64 = await this.blobToBase64(event.blob);
    this.imageEdited.emit(base64);
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  imageLoaded(): void {
    this.showCropper = true;
  }

  cropperReady(): void {
    // cropper ready
  }

  loadImageFailed(): void {
    // show message
  }

  onImageBrowseClick(): void {
    if (DxWidgetUtils.hasInstance(this.fileUploader)) {
      const uploader: any = this.fileUploader.instance;
      uploader._isCustomClickEvent = true;
      uploader._$fileInput[0].click();
    }
  }

  onCropImageClick(): void {
    if (DxWidgetUtils.hasInstance(this.fileUploader)) {
      this.fileUploader.instance.reset();
    }
    this.imageCropper.crop();
  }

  clearImage = (): void => {
    this.selectedImageFile = null;
    this.selectedImageBase64 = null;
    this.showCropper = false;
    this.disableAction = true;
  };

  rotateLeft(): void {
    this.transform = {
      ...this.transform,
      rotate: (this.transform.rotate || 0) - 90
    };
    this.flipAfterRotate();
  }

  rotateRight(): void {
    this.transform = {
      ...this.transform,
      rotate: (this.transform.rotate || 0) + 90
    };
    this.flipAfterRotate();
  }

  flipHorizontal(): void {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH
    };
  }

  flipVertical(): void {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV
    };
  }

  showWebcam(): void {
    this.webcamVisible = true;
    this.showWebcamInfo();
  }

  webcamImageCaptured(data: string): void {
    this.selectedImageFile = null;
    this.selectedImageBase64 = null;
    if (!!data) {
      this.selectedImageBase64 = data;
      this.disableAction = false;
    }
    this.webcamClosed();
  }

  webcamClosed(): void {
    this.webcamVisible = false;
  }

  showWebcamInfo(): void {
    if (this.isIOSDevice) {
      setTimeout(() => {
        this.webcamInfoVisible = true;
      }, 500);
    }
  }

  closeWebcamInfo(): void {
    this.webcamInfoVisible = false;
  }

  private flipAfterRotate(): void {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH
    };
  }
}
